import { AppImage } from '@/components/ui/app-image';
import type { FeedItemPhoto } from '@/types/feed';
import { imageUrl } from '@/utils/image-url';
import { createLogger } from '@/utils/logger';
import { useEffect, useState } from 'react';
// RN Image is imported ONLY for `getSize` (expo-image has no equivalent); all
// rendering goes through AppImage (expo-image) for its memory-disk cache.
// eslint-disable-next-line no-restricted-syntax -- getSize orientation probe only; see photo-orientation.ts
import { Image as RNImage, Pressable, View } from 'react-native';
import {
  getCachedOrientation,
  orientationFromDimensions,
  resolveOrientation,
  type Orientation,
} from './photo-orientation';

const logger = createLogger('PHOTO_GRID');

interface CellHandlers {
  onPhotoPress: (index: number) => void;
  /** Fires on touch-down, before the press; used to prefetch viewer images. */
  onPhotoPressIn?: (index: number) => void;
}

interface PhotoGridProps extends CellHandlers {
  photos: FeedItemPhoto[];
}

function photoUri(photo: FeedItemPhoto): string {
  return imageUrl(photo.r2Key || photo.imageUrl, 'medium');
}

/**
 * A single grid cell image. Size is set via `style` (AppImage does not take a
 * className; NativeWind's interop does not reliably size expo-image). The alt
 * text, when the author gave one, is the image's label; otherwise the image
 * is decorative and the surrounding Pressable carries the label.
 */
function GridImage({ photo }: { photo: FeedItemPhoto }) {
  const a11y = photo.altText
    ? { accessibilityLabel: photo.altText }
    : { decorative: true as const };
  return (
    <AppImage
      r2Key={photo.r2Key || photo.imageUrl}
      preset="medium"
      style={{ width: '100%', height: '100%' }}
      {...a11y}
    />
  );
}

/** One pressable grid cell; `className` carries the layout-specific sizing. */
function GridCell({
  photo,
  index,
  className,
  handlers,
}: {
  photo: FeedItemPhoto;
  index: number;
  className: string;
  handlers: CellHandlers;
}) {
  const base = `View check-in photo ${index + 1}`;
  return (
    <Pressable
      onPressIn={() => handlers.onPhotoPressIn?.(index)}
      onPress={() => handlers.onPhotoPress(index)}
      className={className}
      accessibilityLabel={photo.altText ? `${base}: ${photo.altText}` : base}
    >
      <GridImage photo={photo} />
    </Pressable>
  );
}

/**
 * Orientation per photo: stored dimensions first, then the session cache,
 * then a `getSize` probe. The probe only runs for photos the API returned
 * without dimensions (rows from before the dimension backfill) and is logged
 * so the case can be seen to disappear. Returns null until every photo is known.
 */
function usePhotoOrientations(photos: FeedItemPhoto[]): Orientation[] | null {
  const [probed, setProbed] = useState<ReadonlyMap<string, Orientation>>(() => new Map());

  const known = photos.map(
    (p) =>
      orientationFromDimensions(p.width, p.height) ??
      getCachedOrientation(photoUri(p)) ??
      probed.get(photoUri(p)),
  );
  const missingKey = photos
    .filter((_, i) => known[i] === undefined)
    .map(photoUri)
    .join('\n');

  useEffect(() => {
    if (!missingKey) return;
    const uris = missingKey.split('\n');
    logger.log(`probing ${uris.length} photo(s) without stored dimensions`, uris);

    let cancelled = false;
    Promise.all(
      uris.map((uri) =>
        resolveOrientation(uri, (u, onOk, onErr) => RNImage.getSize(u, onOk, onErr)).then(
          (orientation) => [uri, orientation] as const,
        ),
      ),
    ).then((entries) => {
      if (!cancelled) setProbed((prev) => new Map([...prev, ...entries]));
    });

    return () => {
      cancelled = true;
    };
  }, [missingKey]);

  return known.every((o): o is Orientation => o !== undefined) ? known : null;
}

// -- 2-photo layouts --

function TwoLandscape({ photos, handlers }: LayoutProps) {
  return (
    <View className="flex-row h-48 gap-1 overflow-hidden">
      {photos.map((photo, index) => (
        <GridCell
          key={photo.id}
          photo={photo}
          index={index}
          className="flex-1"
          handlers={handlers}
        />
      ))}
    </View>
  );
}

interface LayoutProps {
  photos: FeedItemPhoto[];
  handlers: CellHandlers;
}

interface OrientedLayoutProps extends LayoutProps {
  orientations: Orientation[];
}

/** Side-by-side row; portrait cells get a 3:4 box. Used for 2 photos and 3 with 2+ portraits. */
function HorizontalRow({ photos, orientations, handlers }: OrientedLayoutProps) {
  return (
    <View className="flex-row gap-1 overflow-hidden">
      {photos.map((photo, index) => (
        <GridCell
          key={photo.id}
          photo={photo}
          index={index}
          className={`flex-1 ${orientations[index] === 'portrait' ? 'aspect-[3/4]' : ''}`}
          handlers={handlers}
        />
      ))}
    </View>
  );
}

// -- 3-photo layouts --

function ThreeLandscape({ photos, handlers }: LayoutProps) {
  return (
    <View className="flex-row h-48 gap-1 overflow-hidden">
      <GridCell photo={photos[0]} index={0} className="flex-1" handlers={handlers} />
      <View className="flex-1 gap-1">
        <GridCell photo={photos[1]} index={1} className="flex-1" handlers={handlers} />
        <GridCell photo={photos[2]} index={2} className="flex-1" handlers={handlers} />
      </View>
    </View>
  );
}

function PortraitPlusStackedLandscapes({ photos, orientations, handlers }: OrientedLayoutProps) {
  const portraitIdx = orientations.findIndex((o) => o === 'portrait');
  const [first, second] = orientations
    .map((o, i) => (o === 'landscape' ? i : -1))
    .filter((i) => i >= 0);

  return (
    <View className="flex-row gap-1 overflow-hidden">
      <GridCell
        photo={photos[portraitIdx]}
        index={portraitIdx}
        className="flex-1 aspect-[3/4]"
        handlers={handlers}
      />
      <View className="flex-1 gap-1">
        <GridCell photo={photos[first]} index={first} className="flex-1" handlers={handlers} />
        <GridCell photo={photos[second]} index={second} className="flex-1" handlers={handlers} />
      </View>
    </View>
  );
}

export function PhotoGrid({ photos, onPhotoPress, onPhotoPressIn }: PhotoGridProps) {
  const orientations = usePhotoOrientations(photos);
  const handlers: CellHandlers = { onPhotoPress, onPhotoPressIn };

  if (photos.length === 0) return null;

  if (photos.length === 1) {
    const isPortrait = orientations?.[0] === 'portrait';
    return (
      <GridCell
        photo={photos[0]}
        index={0}
        className={`overflow-hidden ${isPortrait ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}
        handlers={handlers}
      />
    );
  }

  const portraitCount = orientations?.filter((o) => o === 'portrait').length ?? 0;

  if (photos.length === 2) {
    if (portraitCount === 0 || !orientations) {
      return <TwoLandscape photos={photos} handlers={handlers} />;
    }
    return <HorizontalRow photos={photos} orientations={orientations} handlers={handlers} />;
  }

  // 3+ photos
  if (portraitCount === 0 || !orientations) {
    return <ThreeLandscape photos={photos} handlers={handlers} />;
  }
  if (portraitCount >= 2) {
    return <HorizontalRow photos={photos} orientations={orientations} handlers={handlers} />;
  }
  return (
    <PortraitPlusStackedLandscapes
      photos={photos}
      orientations={orientations}
      handlers={handlers}
    />
  );
}
